import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authClient } from "../../lib/authClient";

type CreateSiweMessageParams = {
  address: string;
  chainId: number;
  nonce: string;
  statement?: string;
};

const createSiweMessage = ({
  address,
  chainId,
  nonce,
  statement = "Sign in with Ethereum to the app.",
}: CreateSiweMessageParams): string => {
  const domain = window.location.host;
  const origin = window.location.origin;
  const issuedAt = new Date().toISOString();

  return `${domain} wants you to sign in with your Ethereum account:
${address}

${statement}

URI: ${origin}
Version: 1
Chain ID: ${chainId}
Nonce: ${nonce}
Issued At: ${issuedAt}`;
};

export function SiweAuth() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [walletAddress, setWalletAddress] = useState<string | null>(null);

  const resolveChainId = async (): Promise<number> => {
    const chainIdHex = await window.ethereum?.request({
      method: "eth_chainId",
    });
    if (typeof chainIdHex === "string") {
      const parsed = Number.parseInt(chainIdHex, 16);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }

    return 1;
  };

  const handleSiweSignIn = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      // Verifica se o MetaMask está disponível
      if (!window.ethereum) {
        setError(
          "MetaMask não detectado. Por favor, instale a extensão MetaMask.",
        );
        return;
      }

      const accounts = await window.ethereum.request({
        method: "eth_requestAccounts",
      }) as string[];

      if (!accounts || accounts.length === 0) {
        setError("Nenhuma conta encontrada. Por favor, conecte sua carteira.");
        return;
      }

      const address = accounts[0];
      setWalletAddress(address);

      const chainId = await resolveChainId().catch((chainError) => {
        setError(
          chainError instanceof Error
            ? chainError.message
            : "Falha ao determinar a rede da carteira.",
        );
        throw chainError;
      });

      // Gera um nonce do servidor
      const { data: nonceData, error: nonceError } = await authClient.siwe
        .nonce({
          walletAddress: address,
          chainId,
        });

      if (nonceError) {
        console.error("Erro ao obter nonce:", nonceError);
        setError(
          `Erro ao obter nonce do servidor: ${
            nonceError.message || "Verifique se o servidor está rodando"
          }`,
        );
        return;
      }

      if (!nonceData?.nonce) {
        console.error("Nonce data:", nonceData);
        setError("Nonce não retornado pelo servidor.");
        return;
      }

      // Cria a mensagem SIWE seguindo o padrão ERC-4361
      const message = createSiweMessage({
        address,
        chainId,
        nonce: nonceData.nonce,
      });

      const signature = await window.ethereum.request({
        method: "personal_sign",
        params: [message, address],
      }) as string;

      const { error: verifyError } = await authClient.siwe.verify({
        message,
        signature,
        walletAddress: address,
        chainId,
      });

      if (verifyError) {
        console.error("Erro na verificação:", verifyError);
        setError(
          verifyError.message ??
            `Falha ao autenticar com Ethereum (Código: ${
              verifyError.code || "desconhecido"
            })`,
        );
        return;
      }

      authClient.$store.notify("$sessionSignal");
      navigate("/profile", { replace: true });
    } catch (error: any) {
      console.error("Erro ao autenticar com SIWE:", error);

      // Trata erros específicos do MetaMask
      if (error.code === 4001) {
        setError("Você rejeitou a solicitação de conexão.");
      } else if (error.code === -32002) {
        setError(
          "Já existe uma solicitação de conexão pendente. Verifique o MetaMask.",
        );
      } else {
        setError("Erro ao autenticar. Tente novamente.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-method-content">
      <p className="auth-method-subtitle">
        Conecte sua carteira Ethereum para fazer login de forma descentralizada
      </p>

      <div className="siwe-info">
        <div className="siwe-icon">
          <svg
            width="48"
            height="48"
            viewBox="0 0 256 417"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M127.961 0L125.666 7.741V284.894L127.961 287.187L255.922 211.956L127.961 0Z"
              fill="#343434"
            />
            <path
              d="M127.962 0L0 211.956L127.962 287.187V153.738V0Z"
              fill="#8C8C8C"
            />
            <path
              d="M127.961 312.187L126.652 313.762V406.923L127.961 410.376L256 237.002L127.961 312.187Z"
              fill="#3C3C3B"
            />
            <path
              d="M127.962 410.376V312.187L0 237.002L127.962 410.376Z"
              fill="#8C8C8C"
            />
            <path
              d="M127.961 287.187L255.922 211.956L127.961 153.738V287.187Z"
              fill="#141414"
            />
            <path
              d="M0 211.956L127.962 287.187V153.738L0 211.956Z"
              fill="#393939"
            />
          </svg>
        </div>
        <div className="siwe-features">
          <div className="feature-item">
            <span className="feature-icon">✓</span>
            <span>Autenticação descentralizada</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon">✓</span>
            <span>Sem necessidade de senha</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon">✓</span>
            <span>Controle total dos seus dados</span>
          </div>
        </div>
      </div>

      {walletAddress && (
        <div className="wallet-address">
          <span>Carteira conectada:</span>
          <code>{walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}</code>
        </div>
      )}

      {error && <p className="auth-error">{error}</p>}

      <button
        type="button"
        onClick={handleSiweSignIn}
        className="primary-button siwe-button"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Conectando..." : "Conectar com Ethereum"}
      </button>

      <p className="siwe-note">
        MetaMask ou outra carteira Web3 é necessária
      </p>
    </div>
  );
}

// Adiciona tipagem para window.ethereum
declare global {
  interface Window {
    ethereum?: {
      request: (args: { method: string; params?: any[] }) => Promise<any>;
      isMetaMask?: boolean;
    };
  }
}
